import { createFileRoute, Link } from "@tanstack/react-router";
import { Code2, Zap, Shield, Globe, Layers, GitBranch, ArrowRight } from "lucide-react";
import { ServicePage } from "@/components/sections/service-page";
import { PageShell } from "@/components/site/page-shell";

export const Route = createFileRoute("/services/web-development/")({
  component: WebDevIndex,
  head: () => ({
    meta: [
      { title: "Web Development Services — CoderLity" },
      { name: "description", content: "Modern, fast, and scalable web applications built with TypeScript, React and edge infrastructure." },
      { property: "og:title", content: "Web Development Services — CoderLity" },
      { property: "og:description", content: "Production-grade web apps engineered for speed, security and scale." },
      { property: "og:url", content: "/services/web-development" },
    ],
    links: [{ rel: "canonical", href: "https://coderlity.com/services/web-development" }],
  }),
});

const stacks = [
  { slug: "react-nextjs", title: "React & Next.js", desc: "Explore react & next.js" },
  { slug: "mern-stack", title: "MERN Stack", desc: "Explore mern stack" },
  { slug: "laravel-php", title: "Laravel / PHP", desc: "Explore laravel / php" },
  { slug: "wordpress", title: "WordPress", desc: "Explore wordpress" },
];

const solutions = [
  { slug: "saas-platforms", title: "SaaS Platforms", desc: "Explore saas platforms" },
  { slug: "marketing-sites", title: "Marketing Sites", desc: "Explore marketing sites" },
  { slug: "web-portals", title: "Web Portals", desc: "Explore web portals" },
  { slug: "progressive-web-apps", title: "Progressive Web Apps", desc: "Explore progressive web apps" },
];

const services = [
  { slug: "api-integration", title: "API Integration", desc: "Explore api integration" },
  { slug: "performance-audits", title: "Performance Audits", desc: "Explore performance audits" },
  { slug: "maintenance-support", title: "Maintenance & Support", desc: "Explore maintenance & support" },
];

function Group({ title, items }: { title: string; items: { slug: string; title: string; desc: string }[] }) {
  return (
    <section className="py-12">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <h2 className="text-3xl font-bold tracking-tight">{title}</h2>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((it) => (
            <Link
              key={it.slug}
              to="/services/web-development/$child"
              params={{ child: it.slug }}
              className="group rounded-2xl border border-border bg-card p-6 hover:border-primary/40 transition"
            >
              <h3 className="text-lg font-semibold">{it.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground inline-flex items-center gap-1">
                {it.desc} <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
              </p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function WebDevIndex() {
  return (
    <>
      <ServicePage data={data} />
      <PageShell>
        <Group title="Stacks" items={stacks} />
        <Group title="Solutions" items={solutions} />
        <Group title="Services" items={services} />
      </PageShell>
    </>
  );
}

const data = {
  eyebrow: "Services",
  title: "Web Development",
  sub: "Modern, fast, and scalable web applications.",
  intro:
    "We build production-grade web platforms that load in under a second, scale to millions of requests, and ship new features weekly. From marketing sites to complex SaaS dashboards, our engineering team blends modern frameworks with edge infrastructure to deliver web experiences that feel native, perform globally, and never go down at 2am.",
  features: [
    { icon: Zap, title: "Sub-second load times", description: "Edge-deployed apps with Core Web Vitals in the green by default." },
    { icon: Code2, title: "Type-safe codebases", description: "Strict TypeScript end-to-end so refactors stay fearless." },
    { icon: Shield, title: "Security baked in", description: "Authentication, RLS policies and signed webhooks from day one." },
    { icon: Globe, title: "Global by default", description: "Multi-region edge runtimes and CDN caching out of the box." },
    { icon: Layers, title: "Modular architecture", description: "Reusable components, design systems and feature flags." },
    { icon: GitBranch, title: "CI/CD pipelines", description: "Preview deploys per branch, automated tests and one-click rollbacks." },
  ],
  process: [
    { step: "01", title: "Discovery", description: "Goals, user flows, scope and success metrics." },
    { step: "02", title: "Architecture", description: "Stack selection, data model and infrastructure design." },
    { step: "03", title: "Build", description: "Iterative sprints with weekly demos and preview links." },
    { step: "04", title: "Launch & scale", description: "Production hardening, monitoring and growth iterations." },
  ],
  deliverables: [
    "Production web application with full source code",
    "Fully typed component library and design tokens",
    "CI/CD pipeline with preview environments",
    "Monitoring, error tracking and uptime alerts",
    "Technical documentation and onboarding guide",
    "30 days of post-launch support",
  ],
  techStack: ["TypeScript", "React 19", "TanStack Start", "Next.js", "Node.js", "PostgreSQL", "Supabase", "Cloudflare Workers", "Tailwind CSS", "Vite"],
  pricing: [
    { name: "Landing site", price: "$2.5k", features: ["Up to 6 pages", "CMS-ready", "SEO + analytics", "2 weeks delivery"] },
    { name: "Web app", price: "$12k", period: "project", highlighted: true, features: ["Auth + database", "Custom dashboards", "API integrations", "6–10 weeks"] },
    { name: "Retainer", price: "$6k", period: "month", features: ["Dedicated engineer", "Weekly releases", "Priority fixes", "Roadmap planning"] },
  ],
  faqs: [
    { q: "Which framework will you use?", a: "We default to React + TypeScript with TanStack Start or Next.js. We pick the stack based on your team's existing skills and your scaling needs." },
    { q: "Do you handle hosting?", a: "Yes — we deploy to Vercel, Cloudflare, or your own AWS/GCP account. You always own the code and the infrastructure." },
    { q: "How fast can we ship?", a: "Landing sites go live in 1–2 weeks. Full web apps typically launch in 6–10 weeks with weekly milestones along the way." },
    { q: "Will the app be SEO-friendly?", a: "Absolutely. We use server-side rendering or static generation, per-route metadata, structured data, and Core Web Vitals optimization." },
  ],
};
